import React, { useEffect, useMemo, useRef } from 'react';
import i18n from '../../../i18n';
import { deriveCanonicalGoalState } from './goalSatisfaction';

const zcT = (key, options) => i18n.t(key, { ns: 'zodiacCrush', ...options });

// frontend/src/games/zodiacCrush/levels/zcLevelGoals.js

// 12 mundos = 12 signos
export const ZC_WORLD_ORDER = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
];

export const ZC_WORLD_LABEL = {
  aries: 'Aries',
  taurus: 'Tauro',
  gemini: 'Géminis',
  cancer: 'Cáncer',
  leo: 'Leo',
  virgo: 'Virgo',
  libra: 'Libra',
  scorpio: 'Escorpio',
  sagittarius: 'Sagitario',
  capricorn: 'Capricornio',
  aquarius: 'Acuario',
  pisces: 'Piscis',
};

export const ZC_WORLD_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',

  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',

  gemini: 'air',
  libra: 'air',
  aquarius: 'air',

  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
};

export const LEVELS_PER_WORLD = 20;
export const TOTAL_LEVELS = 12 * LEVELS_PER_WORLD;

// =========================
// Sign key normalization
// Backend/level-seeds may use Spanish or pluralized sign keys (e.g. "tauro", "geminis").
// Normalize them to our canonical `ZC_WORLD_ORDER` keys.
// =========================
const _stripDiacritics = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const SIGN_ALIASES = {
  // Spanish -> canonical
  aries: 'aries',
  tauro: 'taurus',
  toro: 'taurus',
  taurus: 'taurus',
  gemini: 'gemini',
  geminis: 'gemini',
  géminis: 'gemini',
  cancer: 'cancer',
  cáncer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  scorpio: 'scorpio',
  sagitario: 'sagittarius',
  sagittarius: 'sagittarius',
  capricornio: 'capricorn',
  capricorn: 'capricorn',
  acuario: 'aquarius',
  aquarius: 'aquarius',
  piscis: 'pisces',
  pisces: 'pisces',
};

function normalizeSignKey(input) {
  const raw = _stripDiacritics(String(input || '')).trim().toLowerCase();
  if (!raw) return '';
  // Direct alias
  if (SIGN_ALIASES[raw]) return SIGN_ALIASES[raw];
  // If it's already a canonical key
  if (ZC_WORLD_ORDER.includes(raw)) return raw;
  // Best-effort contains match (handles things like "world:tauro lvl 3")
  for (const k of Object.keys(SIGN_ALIASES)) {
    if (raw.includes(k)) return SIGN_ALIASES[k];
  }
  for (const k of ZC_WORLD_ORDER) {
    if (raw.includes(k)) return k;
  }
  return '';
}

// =========================
// Progress helpers
// =========================
export function initLevelProgress() {
  return {
    score: 0,
    stripesActivated: 0,
    starsCreated: 0,
    cascades: 0,
    clearedFire: 0,
    clearedWater: 0,
    clearedAir: 0,
    clearedEarth: 0,
  };
}

// Progress update: snapshot-safe, avoids double-counting when engine/UI reports cumulative totals.
export function applyLevelProgress(prev, snap) {
  const p = prev && typeof prev === 'object' ? prev : initLevelProgress();
  const d = snap && typeof snap === 'object' ? snap : {};

  // IMPORTANT:
  // The engine/UI often reports cumulative totals so far (score, clearedWater, stripesActivated, etc.).
  // If we *sum* those cumulatives every move, objectives will complete too early.
  // So we treat the incoming object as a snapshot and keep the MAX observed value per metric.
  const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
  const max = (a, b) => Math.max(num(a), num(b));

  return {
    // Score is absolute (current total score)
    score: max(p.score, d.score),

    // These are typically cumulative counters (or at least monotonic). Keep best-so-far.
    stripesActivated: max(p.stripesActivated, d.stripesActivated),
    starsCreated: max(p.starsCreated, d.starsCreated),

    // Cascades in your logs is also a run metric (best / max depth). Keep best-so-far.
    cascades: max(p.cascades, d.cascades),

    // Element clears are cumulative totals so far.
    clearedFire: max(p.clearedFire, d.clearedFire),
    clearedWater: max(p.clearedWater, d.clearedWater),
    clearedAir: max(p.clearedAir, d.clearedAir),
    clearedEarth: max(p.clearedEarth, d.clearedEarth),
  };
}

// =========================
// Level ↔ World mapping
// =========================
// Some callers pass "level within world" (1..20) plus `signKey`.
// Normalize to ABSOLUTE level (1..240) when signKey is valid.
export function normalizeAbsoluteLevel(level, signKey) {
  const lv = Math.max(1, Math.floor(Number(level || 1)));

  const sk = String(signKey || '').trim().toLowerCase();
  const wi = sk ? ZC_WORLD_ORDER.indexOf(sk) : -1;

  // If caller provided a valid signKey and level looks like "within world", map it to absolute.
  if (wi >= 0 && lv >= 1 && lv <= LEVELS_PER_WORLD) {
    const abs = wi * LEVELS_PER_WORLD + lv;
    return Math.max(1, Math.min(TOTAL_LEVELS, abs));
  }

  // Otherwise, treat `level` as already absolute.
  return Math.max(1, Math.min(TOTAL_LEVELS, lv));
}

export function getWorldIndexForLevel(level, signKey) {
  const abs = normalizeAbsoluteLevel(level, signKey);
  return Math.min(11, Math.floor((abs - 1) / LEVELS_PER_WORLD));
}

export function getIndexInWorld(level, signKey) {
  const abs = normalizeAbsoluteLevel(level, signKey);
  return ((abs - 1) % LEVELS_PER_WORLD) + 1; // 1..20
}

export function getWorldKeyForLevel(level, signKey) {
  return ZC_WORLD_ORDER[getWorldIndexForLevel(level, signKey)] || 'aries';
}

export function getWorldElementForLevel(level, signKey) {
  const w = getWorldKeyForLevel(level, signKey);
  return ZC_WORLD_ELEMENT[w] || 'neutral';
}

// =========================
// Goal builder
// =========================
function clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function makeGoal({ id, type, target, title, element }) {
  const t = Number(target || 0);

  const getCurrent = (prog) => {
    const p = prog && typeof prog === 'object' ? prog : {};
    if (type === 'score_reach') return Number(p.score || 0);
    if (type === 'activate_stripe') return Number(p.stripesActivated || 0);
    if (type === 'make_star') return Number(p.starsCreated || 0);
    if (type === 'cascades_reach') return Number(p.cascades || 0);

    if (type === 'clear_element_fire') return Number(p.clearedFire || 0);
    if (type === 'clear_element_water') return Number(p.clearedWater || 0);
    if (type === 'clear_element_air') return Number(p.clearedAir || 0);
    if (type === 'clear_element_earth') return Number(p.clearedEarth || 0);

    return 0;
  };

  const isDone = (prog) => getCurrent(prog) >= t;

  return {
    id: String(id),
    type,
    target: t,
    title: title || '',
    element: element || null,
    getCurrent,
    isDone,
  };
}

function scoreTarget(worldIndex, idxInWorld) {
  // Crece por mundo + por nivel, calibrado para 20 moves.
  const base = 250 + worldIndex * 140;
  const step = 70 + Math.floor(worldIndex * 6);
  return base + (idxInWorld - 1) * step;
}

function stripesTarget(worldIndex, idxInWorld) {
  // Rampa suave: 2..10 aprox
  const base = 2 + Math.floor(worldIndex / 2);
  const extra = Math.floor((idxInWorld - 1) / 4); // sube cada 4 niveles
  return clamp(base + extra, 2, 12);
}

function starsTarget(worldIndex, idxInWorld) {
  // 1..4 aprox
  const base = worldIndex >= 4 ? 2 : 1;
  const extra = idxInWorld >= 15 ? 1 : 0;
  return clamp(base + extra, 1, 4);
}

function cascadesTarget(worldIndex, idxInWorld) {
  // 2..7 aprox
  // NOTE: We treat "cascades" as a depth/best-run metric (e.g., 1 = only the initial clear).
  // Starting at 2 prevents completing the goal on the very first match.
  const base = 2 + Math.floor(worldIndex / 3);
  const extra = idxInWorld >= 10 ? 1 : 0;
  const extra2 = idxInWorld >= 18 ? 1 : 0;
  return clamp(base + extra + extra2, 2, 7);
}

function elementClearTarget(worldIndex, idxInWorld) {
  // 25..120 aprox
  const base = 25 + worldIndex * 6;
  const step = 5 + Math.floor(worldIndex / 2);
  return base + (idxInWorld - 1) * step;
}

// =========================
// 240-level generator (12 worlds × 20)
// =========================
function buildGoalsForLevel(level, signKey) {
  const absLevel = normalizeAbsoluteLevel(level, normalizeSignKey(signKey));

  const worldIndex = getWorldIndexForLevel(absLevel);
  const idx = getIndexInWorld(absLevel);
  const worldKey = getWorldKeyForLevel(absLevel);
  const worldLabel = zcT(`signs.${worldKey}`, { defaultValue: ZC_WORLD_LABEL[worldKey] || zcT('world') });
  const el = getWorldElementForLevel(absLevel);

  const score = scoreTarget(worldIndex, idx);
  const stripes = stripesTarget(worldIndex, idx);
  const stars = starsTarget(worldIndex, idx);
  const cascades = cascadesTarget(worldIndex, idx);
  const clears = elementClearTarget(worldIndex, idx);

  const elementType =
    el === 'fire'
      ? 'clear_element_fire'
      : el === 'water'
        ? 'clear_element_water'
        : el === 'air'
          ? 'clear_element_air'
          : el === 'earth'
            ? 'clear_element_earth'
            : null;

  // Onboarding por mundo (cada mundo re-enseña con variación)
  // Estructura dentro del mundo (1..20):
  // 1) score
  // 2) stripes
  // 3) clear element
  // 4) score
  // 5) star
  // 6) stripes
  // 7) cascades
  // 8) clear element
  // 9) score
  // 10) score + stripes
  // 11) clear element + cascades
  // 12) star + stripes
  // 13) score
  // 14) clear element
  // 15) score + star
  // 16) stripes + cascades
  // 17) score
  // 18) clear element + stripes
  // 19) score + cascades
  // 20) boss: score + stripes + star (o element si querés)
  const goals = [];

  const idBase = `${worldKey}_lvl${String(absLevel)}`;

  const pushScore = (target, suffix = '') =>
    goals.push(
      makeGoal({
        id: `${idBase}_score${suffix}`,
        type: 'score_reach',
        target,
        title: zcT('goals.score', { target }),
      })
    );

  const pushStripes = (target, suffix = '') =>
    goals.push(
      makeGoal({
        id: `${idBase}_stripes${suffix}`,
        type: 'activate_stripe',
        target,
        title: zcT('goals.stripe', { target }),
      })
    );

  const pushStars = (target, suffix = '') =>
    goals.push(
      makeGoal({
        id: `${idBase}_stars${suffix}`,
        type: 'make_star',
        target,
        title: zcT('goals.star', { target }),
      })
    );

  const pushCascades = (target, suffix = '') =>
    goals.push(
      makeGoal({
        id: `${idBase}_cascades${suffix}`,
        type: 'cascades_reach',
        target,
        title: zcT('goals.cascade', { target }),
      })
    );

  const pushElement = (target, suffix = '') => {
    if (!elementType) return;
    const elLabel =
      el === 'fire' ? `${zcT('elements.fire')} 🔥` :
      el === 'water' ? `${zcT('elements.water')} 💧` :
      el === 'air' ? `${zcT('elements.air')} 🌪️` :
      el === 'earth' ? `${zcT('elements.earth')} 🌿` : zcT('element');

    goals.push(
      makeGoal({
        id: `${idBase}_el${suffix}`,
        type: elementType,
        target,
        title: zcT('goals.clear', { element: elLabel, target }),
        element: el,
      })
    );
  };

  // Ajustes finos por mundo: a partir de mundos posteriores, pedimos un poquito más
  const scoreHard = Math.round(score * (worldIndex >= 6 ? 1.08 : worldIndex >= 3 ? 1.04 : 1.0));
  const clearsHard = Math.round(clears * (worldIndex >= 6 ? 1.10 : 1.0));

  switch (idx) {
    case 1:
      // Aries 1 (y nivel 1 de cada mundo): solo score, simple y claro
      pushScore(score);
      break;

    case 2:
      // Aries 2: rayos (NO repetir score)
      pushStripes(Math.max(2, stripes));
      break;

    case 3:
      pushElement(Math.max(22, clearsHard));
      break;

    case 4:
      pushScore(scoreHard);
      break;

    case 5:
      pushStars(Math.max(1, stars));
      break;

    case 6:
      pushStripes(stripes + 1);
      break;

    case 7:
      pushCascades(Math.max(1, cascades));
      break;

    case 8:
      pushElement(clearsHard + 8);
      break;

    case 9:
      pushScore(scoreHard + 120);
      break;

    case 10:
      pushScore(scoreHard + 160, '_a');
      pushStripes(Math.max(2, stripes + 1), '_b');
      break;

    case 11:
      pushElement(clearsHard + 14, '_a');
      pushCascades(Math.max(1, cascades + 1), '_b');
      break;

    case 12:
      pushStars(Math.max(1, stars), '_a');
      pushStripes(Math.max(2, stripes), '_b');
      break;

    case 13:
      pushScore(scoreHard + 230);
      break;

    case 14:
      pushElement(clearsHard + 22);
      break;

    case 15:
      pushScore(scoreHard + 260, '_a');
      pushStars(Math.max(1, stars + 1), '_b');
      break;

    case 16:
      pushStripes(Math.max(2, stripes + 2), '_a');
      pushCascades(Math.max(1, cascades + 1), '_b');
      break;

    case 17:
      pushScore(scoreHard + 320);
      break;

    case 18:
      pushElement(clearsHard + 30, '_a');
      pushStripes(Math.max(2, stripes + 2), '_b');
      break;

    case 19:
      pushScore(scoreHard + 360, '_a');
      pushCascades(Math.max(1, cascades + 2), '_b');
      break;

    case 20:
      // Boss del mundo: 3 objetivos
      pushScore(scoreHard + 420, '_a');
      pushStripes(Math.max(2, stripes + 3), '_b');
      pushStars(Math.max(1, stars + (worldIndex >= 6 ? 1 : 0)), '_c');
      break;

    default:
      // fallback (no debería pasar)
      pushScore(scoreHard);
      break;
  }

  // Seguridad: si por alguna razón no hubo goals (element neutral), poner score
  if (!goals.length) {
    pushScore(scoreHard);
  }

  // Debug-friendly: título del mundo (opcional, no se usa en UI)
  goals.forEach((g) => {
    if (!g.title) g.title = `${worldLabel} · ${g.type} · ${g.target}`;
  });

  return goals;
}

// -------------------------
// Robust level parsing
// Some callers may pass strings like "aries-2" or objects like { level: 2 }.
// If we fail to parse, we keep a safe fallback (1).
// -------------------------
function coerceLevelNumber(input) {
  if (input == null) return 1;

  // If caller sends nested objects (e.g. { level: { level: 2 } })
  if (typeof input === 'object') {
    if (input.level != null) return coerceLevelNumber(input.level);
    if (input.id != null) return coerceLevelNumber(input.id);
    if (input.key != null) return coerceLevelNumber(input.key);
    // last resort: try stringifying
    return coerceLevelNumber(String(input));
  }

  // Numeric already
  if (typeof input === 'number') {
    const n = Math.floor(input);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  // Strings like "2", "lvl 2", "aries-2"
  const s = String(input).trim();
  if (!s) return 1;

  const direct = Number(s);
  if (Number.isFinite(direct)) {
    const n = Math.floor(direct);
    return n > 0 ? n : 1;
  }

  const m = s.match(/\d+/);
  if (m) {
    const n = parseInt(m[0], 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  return 1;
}

function coerceSignKey(input, fallback) {
  // Prefer explicit fallback first
  const fb = normalizeSignKey(fallback);
  if (fb) return fb;

  // Try objects like { signKey: 'tauro' } or { worldKey: 'geminis' }
  if (input && typeof input === 'object') {
    const cand =
      input.signKey ||
      input.worldKey ||
      input.world ||
      input.sign ||
      (input.level && (input.level.signKey || input.level.worldKey || input.level.world || input.level.sign)) ||
      '';
    const s = normalizeSignKey(cand);
    if (s) return s;
  }

  // Try strings like "tauro-3", "geminis 4", "world:cáncer lvl 2"
  const s2 = normalizeSignKey(input);
  if (s2) return s2;

  return '';
}
// API principal usada por tu UI
export function getGoalsForLevel(level, signKey) {
  const lv = Math.max(1, Math.floor(coerceLevelNumber(level)));
  const sk = coerceSignKey(level, signKey);
  // buildGoalsForLevel already calls normalizeAbsoluteLevel(level, signKey)
  // so passing lv here is safe and avoids falling back to 1 when `level` was a string/object.
  return buildGoalsForLevel(lv, sk);
}

// =========================
// UI helpers
// =========================
export function goalsDoneFraction(goals, levelProgress) {
  const list = Array.isArray(goals) ? goals : [];
  const total = list.length;
  let done = 0;

  for (const g of list) {
    try {
      if (g && typeof g.isDone === 'function' && g.isDone(levelProgress)) done += 1;
    } catch (_) {}
  }
  return { done, total };
}

function normalizePresentationGoalType(rawType) {
  const t = String(rawType || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!t) return '';
  if (t === 'score' || t === 'score_reach' || t === 'reach_score') return 'score';
  if (t === 'stripesactivated' || t === 'stripes_activated' || t === 'activate_stripe' || t === 'stripe' || t === 'stripes') return 'stripesActivated';
  if (t === 'starscreated' || t === 'stars_created' || t === 'make_star' || t === 'star' || t === 'stars') return 'starsCreated';
  if (t === 'cascades' || t === 'cascade' || t === 'cascades_reach' || t === 'reach_cascades' || t === 'max_cascade' || t === 'max_cascades') return 'cascades';
  if (t === 'clear_element_fire' || t === 'clear_fire' || t === 'fire') return 'clear_element_fire';
  if (t === 'clear_element_water' || t === 'clear_water' || t === 'water') return 'clear_element_water';
  if (t === 'clear_element_air' || t === 'clear_air' || t === 'air') return 'clear_element_air';
  if (t === 'clear_element_earth' || t === 'clear_earth' || t === 'earth') return 'clear_element_earth';
  return '';
}

export function formatGoalIcon(goal) {
  const type = normalizePresentationGoalType(goal?.type);
  return {
    score: '🎯',
    stripesActivated: '⚡',
    starsCreated: '🌟',
    cascades: '🌊',
    clear_element_fire: '🔥',
    clear_element_water: '💧',
    clear_element_air: '🌬️',
    clear_element_earth: '🪨',
  }[type] || '🎯';
}

function localizedGoalText(type, target) {
  const targetValue = Math.max(0, Number(target || 0) || 0);
  if (type === 'score') return zcT('goals.scoreAction', { target: targetValue });
  if (type === 'stripesActivated') return zcT('goals.stripeAction', { target: targetValue, count: targetValue });
  if (type === 'starsCreated') return zcT('goals.starAction', { target: targetValue, count: targetValue });
  if (type === 'cascades') return zcT('goals.cascadeAction', { target: targetValue, count: targetValue });

  const elements = {
    clear_element_fire: ['elements.fire', '🔥'],
    clear_element_water: ['elements.water', '💧'],
    clear_element_air: ['elements.air', '🌬️'],
    clear_element_earth: ['elements.earth', '🪨'],
  };
  const element = elements[type];
  if (element) return zcT('goals.clearAction', { element: zcT(element[0]), target: targetValue, count: targetValue });
  return zcT('objective');
}

export function formatGoalDescription(goal) {
  return localizedGoalText(normalizePresentationGoalType(goal?.type), goal?.target);
}

export function formatGoalProgressValue(goal, levelProgress) {
  if (!goal) return '';

  const curRaw = typeof goal.getCurrent === 'function' ? goal.getCurrent(levelProgress) : 0;
  const cur = Number(curRaw || 0) || 0;

  const tgtRaw = Number(goal.target || 0);
  const tgt = Number.isFinite(tgtRaw) && tgtRaw > 0 ? tgtRaw : 0;
  return tgt ? `${Math.min(cur, tgt)}/${tgt}` : '';
}

export function formatGoalAriaLabel(goal, levelProgress) {
  const description = formatGoalDescription(goal);
  const progress = formatGoalProgressValue(goal, levelProgress);
  return progress ? `${description}. ${zcT('goalProgress', { current: progress.split('/')[0], target: progress.split('/')[1] })}` : description;
}

export function formatGoalProgressText(goal, levelProgress) {
  const description = formatGoalDescription(goal);
  const progress = formatGoalProgressValue(goal, levelProgress);
  return progress ? `${description} ${progress}` : description;
}

// =========================
// UI Component (default export)
// =========================
export default function ZodiacCrushLevelGoals({
  level,
  signKey,
  goals: goalsProp,
  nonce,
  levelProgress,
  canonicalGoalState: canonicalGoalStateProp,
  onGoalCompleted,
}) {
// NOTE: `level` can sometimes be passed as a mutable object from parents.
// If the object is mutated in-place, `useMemo([level])` would NOT re-run and goals would appear “stuck”.
// Coercion is cheap, so compute it directly every render to guarantee freshness.
const parsedLevel = coerceLevelNumber(level);
const parsedSignKey = coerceSignKey(level, signKey);

const computedGoals = useMemo(() => {
  // `nonce` is a deliberate cache-buster when navigating Home -> ZodiacCrush in SPA mode.
  // Referencing it here makes the dependency real (and keeps eslint happy).
  void nonce;
  return getGoalsForLevel(parsedLevel, parsedSignKey);
}, [parsedLevel, parsedSignKey, nonce]);

// If parent passes authoritative goals, prefer them. Otherwise fallback to computed goals.
const goalsList = useMemo(() => {
  const incoming = Array.isArray(goalsProp) ? goalsProp : [];
  return incoming.length ? incoming : (Array.isArray(computedGoals) ? computedGoals : []);
}, [goalsProp, computedGoals]);

const derivedCanonicalGoalState = useMemo(
  () => deriveCanonicalGoalState({ goals: goalsList, runProgress: levelProgress }),
  [goalsList, levelProgress]
);
const canonicalState = canonicalGoalStateProp || derivedCanonicalGoalState;
const canonicalGoals = Array.isArray(canonicalState?.goals) ? canonicalState.goals : goalsList;
const frac = useMemo(
  () => ({
    done: Number(canonicalState?.done || 0),
    total: Number(canonicalState?.total || canonicalGoals.length || 0),
  }),
  [canonicalState, canonicalGoals.length]
);

  // Avoid double-calling callbacks (React StrictMode / rerenders)
  // IMPORTANT: reset when level/sign changes even if `total` stays the same.
  const prevDoneRef = useRef(0);
  const prevTotalRef = useRef(0);
  const prevKeyRef = useRef('');

  const signKeyNorm = useMemo(() => String(parsedSignKey || ''), [parsedSignKey]);

  const levelKey = useMemo(
    () => `${String(parsedLevel)}::${signKeyNorm}`,
    [parsedLevel, signKeyNorm]
  );

  useEffect(() => {
    const done = Number(frac?.done || 0);
    const total = Number(frac?.total || 0);

    // If the level/sign changed, reset tracking and DON'T toast/callback on initial paint.
    if (prevKeyRef.current !== levelKey) {
      prevKeyRef.current = levelKey;
      prevTotalRef.current = total;
      prevDoneRef.current = done;
      return;
    }

    // Initialize refs on first run
    if (prevTotalRef.current === 0 && total > 0) {
      prevTotalRef.current = total;
      prevDoneRef.current = done;
      return;
    }

    // If total changed (goals schema changed), reset tracking
    if (total !== prevTotalRef.current) {
      prevTotalRef.current = total;
      prevDoneRef.current = done;
      return;
    }

    // Fire only when done increases
    if (done > prevDoneRef.current) {
      prevDoneRef.current = done;
      if (typeof onGoalCompleted === 'function') {
        onGoalCompleted({ done, total });
      }
    }
  }, [frac?.done, frac?.total, onGoalCompleted, levelKey]);

  if (!Array.isArray(goalsList) || goalsList.length === 0) return null;

  const done = Number(frac?.done || 0);
  const total = Number(frac?.total || 0);

  const cardStyle = {
    borderRadius: 16,
    padding: '12px 12px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 14px 34px rgba(0,0,0,0.24)',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8,
  };

  const titleStyle = {
    fontWeight: 950,
    letterSpacing: 0.4,
    color: 'rgba(255,255,255,0.92)',
    textTransform: 'uppercase',
    fontSize: 12,
  };

  const badgeStyle = {
    fontWeight: 900,
    color: 'rgba(255,255,255,0.88)',
    fontSize: 12,
    opacity: 0.95,
  };

  const rowStyle = (isDone) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 12,
    background: isDone
      ? 'linear-gradient(135deg, rgba(218,165,32,0.18), rgba(75,0,130,0.14))'
      : 'rgba(255,255,255,0.05)',
    border: isDone
      ? '1px solid rgba(218,165,32,0.20)'
      : '1px solid rgba(255,255,255,0.10)',
  });

  const leftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  };

  const textStyle = {
    fontWeight: 850,
    color: 'rgba(255,255,255,0.92)',
    fontSize: 13,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  };

  const listStyle = {
    display: 'grid',
    gap: 8,
  };

  return (
    <div className="zc-goalsCard" style={cardStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>{zcT('objective')}</div>
        <div style={badgeStyle}>{done}/{total}</div>
      </div>

      <div className="zc-goalsList" style={listStyle}>
        {canonicalGoals.map((g) => {
          const isDone = typeof g?.canonicalDone === 'boolean'
            ? g.canonicalDone
            : (typeof g?.isDone === 'function' ? g.isDone(levelProgress) : false);
          const description = formatGoalDescription(g);
          const progress = typeof g?.canonicalCurrent === 'number'
            ? `${Math.min(g.canonicalCurrent, Number(g.target) || 0)}/${Number(g.target) || 0}`
            : formatGoalProgressValue(g, levelProgress);
          const ariaLabel = progress
            ? `${description}. ${zcT('goalProgress', { current: progress.split('/')[0], target: progress.split('/')[1] })}`
            : formatGoalAriaLabel(g, levelProgress);
          return (
            <div key={String(g?.id || `${g?.type}_${g?.target}`)} style={rowStyle(isDone)} aria-label={ariaLabel}>
              <div style={leftStyle}>
                <div style={{ width: 20, textAlign: 'center' }}>{isDone ? '✅' : formatGoalIcon(g)}</div>
                <div style={textStyle} title={description}>{description}</div>
              </div>
              <div style={{ fontWeight: 900, opacity: 0.9, whiteSpace: 'nowrap' }}>{progress}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
